import { useEffect, useState } from 'react';
import {
  getDonationWidgetSettings,
  DEFAULT_DONATION_WIDGET,
  type DonationWidgetSettings,
} from '@/services/finances';

function toWhatsAppLink(number: string): string {
  const digits = number.replace(/\D/g, '');
  return `https://wa.me/${digits}`;
}

export default function FloatingDonationDog() {
  const [settings, setSettings] = useState<DonationWidgetSettings>(DEFAULT_DONATION_WIDGET);
  const [open, setOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [toastId, setToastId] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getDonationWidgetSettings().then((s) => {
      if (!cancelled) setSettings(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showToast) return;
    const t = window.setTimeout(() => setShowToast(false), 6000);
    return () => window.clearTimeout(t);
  }, [showToast, toastId]);

  if (dismissed || !settings.enableDonationDog) return null;

  function closeModal() {
    setOpen(false);
  }

  function handleDonated() {
    setOpen(false);
    setShowToast(true);
    setToastId((n) => n + 1);
  }

  const hasWeb = settings.foundationUrl.trim().length > 0;
  const hasWhatsapp = settings.whatsapp.trim().length > 0;
  const dogImage = settings.donationDogImageUrl.trim();

  return (
    <>
      {/* Perrito flotante */}
      <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="max-w-[260px] bg-white border border-background-200 rounded-2xl rounded-br-sm px-4 py-2.5 text-left cursor-pointer hover:border-accent-300 transition-colors animate-bubble-float"
        >
          <p className="text-sm font-medium text-foreground-800 leading-snug">{settings.bannerText}</p>
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Abrir donaciones"
            className="w-16 h-16 rounded-full bg-accent-500 hover:bg-accent-600 transition-colors flex items-center justify-center cursor-pointer ring-4 ring-accent-100 animate-dog-walk overflow-hidden"
          >
            {dogImage ? (
              <img src={dogImage} alt="Ayúdanos a donar" className="w-full h-full object-cover object-top" />
            ) : (
              <span className="text-3xl leading-none">🐶</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Ocultar perrito"
            className="absolute -top-1 -left-1 w-6 h-6 rounded-full bg-white border border-background-200 text-foreground-500 hover:text-foreground-800 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Modal de donación */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal}></div>
          <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl border border-background-200">
            <div className="px-6 pt-6 pb-4 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3 overflow-hidden">
                {dogImage ? (
                  <img src={dogImage} alt="" className="w-full h-full object-cover object-top" />
                ) : (
                  <span className="text-4xl leading-none">🐶</span>
                )}
              </div>
              <h2 className="text-lg font-bold text-foreground-950">{settings.foundationName}</h2>
              {settings.foundationDesc && (
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{settings.foundationDesc}</p>
              )}
              <button
                type="button"
                onClick={closeModal}
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Cerrar"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 pb-6 space-y-3">
              {hasWeb && (
                <a
                  href={settings.foundationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-global-line text-base"></i> Web Oficial
                </a>
              )}

              {hasWhatsapp && (
                <a
                  href={toWhatsAppLink(settings.whatsapp)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-emerald-500 text-white font-semibold text-sm hover:bg-emerald-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base"></i> Contactar por WhatsApp
                </a>
              )}

              {settings.bankDetails.trim().length > 0 && (
                <div className="rounded-xl bg-secondary-50 border border-secondary-200 p-4">
                  <p className="text-xs font-semibold text-secondary-700 uppercase tracking-wide mb-2">
                    <i className="ri-bank-card-line mr-1.5"></i>Datos de transferencia
                  </p>
                  <p className="text-sm text-foreground-800 whitespace-pre-wrap leading-relaxed">
                    {settings.bankDetails}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleDonated}
                className="w-full py-3 rounded-xl bg-accent-500 text-white font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                ¡Ya doné! 🐾
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast de agradecimiento */}
      {showToast && (
        <div className="fixed bottom-24 right-5 z-50 max-w-xs animate-toast-pop">
          <div className="flex items-start gap-3 bg-white border border-accent-300 rounded-2xl px-4 py-3">
            <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {dogImage ? (
                <img src={dogImage} alt="" className="w-full h-full object-cover object-top" />
              ) : (
                <span className="text-2xl leading-none">🐶</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground-900 leading-snug">{settings.thankyouText}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowToast(false)}
              className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
              aria-label="Cerrar"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
}